"use client";

import React, { useEffect } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader, Sparkles } from "lucide-react";
const SERVICE_OPTIONS = ["Digital Transformation", "Business Strategy", "AI Solutions", "Financial Advisory", "Growth Marketing", "Other Inquiry"];
function ConciergeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Inquiry Received", {
        description: successMessage
      });
    }
    if (submitError) {
      toast.error("Submission Failed", {
        description: submitError
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-6 flex size-16 items-center justify-center rounded-lg bg-primary/20 text-primary">
          <Sparkles className="size-8" />
        </div>
        <h3 className="mb-2 font-default text-3xl font-bold text-foreground">
          Consultation Booked
        </h3>
        <p className="mb-8 max-w-md text-muted-foreground">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
          Send Another Inquiry
        </Button>
      </div>;
  }
  const inputClasses = "flex h-10 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors backdrop-blur-md";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default text-sm font-semibold uppercase tracking-wider">
                Full Name
              </FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Alexander Wright" className={inputClasses} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default text-sm font-semibold uppercase tracking-wider">
                Corporate Email
              </FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="alex@company.com" className={inputClasses} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default text-sm font-semibold uppercase tracking-wider">
                Phone Number
              </FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+61 4XX XXX XXX" className={inputClasses} />
              </FormControl>
              <FormDescription className="text-muted-foreground font-mono text-[10px]">
                Include international prefix
              </FormDescription>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default text-sm font-semibold uppercase tracking-wider">
                Requested Date
              </FormLabel>
              <FormControl>
                <input {...field} type="date" className={inputClasses + " [color-scheme:dark]"} />
              </FormControl>
              <FormDescription className="text-muted-foreground font-mono text-[10px]">
                Select your preferred timeline
              </FormDescription>
              <FormMessage className="text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default text-sm font-semibold uppercase tracking-wider">
              Interested Service
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="h-10 w-full border-border bg-card/40 text-foreground hover:bg-card/60 focus:ring-primary/50 data-[placeholder]:text-muted-foreground/50 rounded-md backdrop-blur-md">
                  <SelectValue placeholder="Select a strategic pillar" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="border-border bg-card text-foreground">
                {SERVICE_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i} className="focus:bg-primary/20 focus:text-primary">
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default text-sm font-semibold uppercase tracking-wider">
              Project Goals & Objectives
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Describe your vision for digital transformation or AI integration..." className="min-h-[120px] resize-y border-border bg-card/40 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-primary/50 rounded-md backdrop-blur-md" />
            </FormControl>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary py-6 text-base font-bold text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all duration-300 sm:w-auto sm:px-12 rounded-md">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Analyzing Request...
          </> : "Initiate Consultation"}
      </Button>
    </Form>;
}
export default function ConsultationInquiry() {
  return <section data-section-id="224800" id="consultation-inquiry" className="relative overflow-hidden bg-background py-20 lg:py-32">
      <div className="pointer-events-none absolute inset-0 opacity-20">
        <div className="absolute -top-[20%] -right-[10%] size-[600px] rounded-lg bg-primary blur-[140px]" />
        <div className="absolute -bottom-[20%] -left-[10%] size-[600px] rounded-lg bg-secondary blur-[140px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 font-default text-4xl font-black tracking-tighter text-foreground md:text-6xl">
              Consultation Inquiry
            </h2>
            <p className="mx-auto max-w-xl text-lg text-muted-foreground font-default">
              Partner with Future Mind Consulting to architect your digital future. Our experts are ready to scale your innovation.
            </p>
          </div>

          <div className="rounded-3xl border border-white/10 bg-card/60 p-6 backdrop-blur-xl shadow-2xl sm:p-10">
            <FormProvider formKey="consultation_form_connect" formId="consultation_form_connect" sectionName="Consultation Inquiry" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            service_type: "",
            preferred_date: "",
            details: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            service_type: {
              required: true
            }
          }} submitText="Initiate Consultation" successMessage="Your inquiry has been successfully transmitted. A senior consultant from FMC will reach out within 24 hours.">
              <ConciergeForm />
            </FormProvider>
          </div>
        </div>
      </div>
      <Toaster position="top-center" />
    </section>;
}